import { useRef, useImperativeHandle, forwardRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

export interface InvisibleReCaptchaHandle {
  /**
   * Executes the invisible reCAPTCHA flow.
   * - Triggers the reCAPTCHA challenge (usually silently)
   * - Returns the generated token if successful
   * - Notifies parent via `onVerify`
   * - Resets the widget afterward to allow future calls
   */
  execute: () => Promise<string | null>;
}

interface InvisibleReCaptchaProps {
  /**
   * Called when a valid token is received (optional)
   */
  onVerify?: (token: string | null) => void;
}

/**
 * Invisible reCAPTCHA
 * - No visible UI (size="invisible")
 * - Executes programmatically via ref.current.execute()
 */
export const InvisibleReCaptcha = forwardRef<InvisibleReCaptchaHandle, InvisibleReCaptchaProps>(
  ({ onVerify }, ref) => {
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    /**
     * Resets the reCAPTCHA instance to allow re-execution.
     * This should always be called after a verification attempt (success or fail).
     */
    const resetReCaptcha = () => {
      try {
        recaptchaRef.current?.reset();
      } catch (err) {
        // Silently handle reset errors
      }
    };

    useImperativeHandle(ref, () => ({
      async execute(): Promise<string | null> {
        if (!siteKey) {
          // No site key configured, return null
          return null;
        }

        try {
          // Request a verification token from Google reCAPTCHA
          const token = await recaptchaRef.current?.executeAsync();

          // Pass token back to the parent callback (null if missing)
          onVerify?.(token || null);

          // Reset for the next execution attempt
          resetReCaptcha();

          // Return token to allow awaiting in caller (optional)
          return token || null;
        } catch (err) {
          // Notify parent that verification failed
          onVerify?.(null);

          // Ensure the widget is reset even after errors
          resetReCaptcha();

          return null;
        }
      },
    }));

    if (!siteKey) {
      return null;
    }

    return <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={siteKey} onChange={onVerify} />;
  },
);

InvisibleReCaptcha.displayName = `InvisibleReCaptcha`;
